import React from 'react';
import { useTranslateRegions } from '@hero/api-hooks';
import { cn } from '@hero/web-lib';
import type { Language } from '@hero/schema';

export interface RegionLaneEntry {
  /** Canonical `customCode` (preferred). Resolved through the 5-layer cache. */
  customCode?: string | null;
  /** Snapshot display name. Used as fallback when `customCode` is empty / not in cache. */
  displayName?: string | null;
}

export interface RegionLaneProps {
  /** Ordered list of stops — origin first, then destinations. */
  stops: ReadonlyArray<RegionLaneEntry | null | undefined>;
  /** Force a specific language. Defaults to the current `i18next` language. */
  lang?: Language;
  /** Separator between stops. Default `' → '` (web). */
  separator?: React.ReactNode;
  /** Extra Tailwind classes. */
  className?: string;
  /** Optional wrapping element. */
  as?: keyof React.JSX.IntrinsicElements;
  /** Render fallback when the list is empty (default: empty string). */
  emptyFallback?: React.ReactNode;
}

/**
 * Multi-stop region chain rendered in the viewer's language.
 *
 * Hides the batching ergonomics of `useTranslateRegions` from consumers — every
 * stop in a route or destination chain lands in a SINGLE translation lookup,
 * so a 10-stop multi-leg order still emits one round-trip (instead of one per
 * `<RegionName />`).
 *
 * Each entry can carry a `customCode` (preferred) and / or a snapshot
 * `displayName` (fallback). When the static bundle / network cache resolves
 * the code, the localized name renders; otherwise the snapshot keeps the UI
 * honest. Both fields are optional so this works against legacy snapshots
 * that only carry one of them.
 *
 * @example
 *   <RegionLane stops={route.destinations} className="font-medium" />
 *   <RegionLane stops={[origin, ...stops]} separator=" • " />
 */
export const RegionLane: React.FC<RegionLaneProps> = ({
  stops,
  lang,
  separator = ' → ',
  className,
  as: Tag = 'span',
  emptyFallback = '',
}) => {
  const codes = stops
    .map(s => s?.customCode)
    .filter((code): code is string => !!code);
  const { display } = useTranslateRegions(codes, lang);

  const labels = stops
    .map(stop => {
      if (!stop) return undefined;
      if (stop.customCode) {
        const resolved = display(stop.customCode);
        // `display()` returns the raw code on miss — prefer the snapshot when
        // we have one so the user never sees a slug.
        if (resolved && resolved !== stop.customCode) return resolved;
        return stop.displayName ?? resolved;
      }
      return stop.displayName ?? undefined;
    })
    .filter((label): label is string => !!label);

  if (labels.length === 0) {
    return <Tag className={cn(className)}>{emptyFallback}</Tag>;
  }

  return (
    <Tag className={cn(className)}>
      {labels.map((label, i) => (
        <React.Fragment key={i}>
          {i > 0 ? separator : null}
          {label}
        </React.Fragment>
      ))}
    </Tag>
  );
};
